import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ProviderApiTokenCreateInput,
  ProviderApiTokenCreateResponse,
  ProviderPortalLinkPayload,
  ProviderPortalLinkResponse,
  ProviderSsoConfig,
} from '@/types/provider-api-token';
import { providerApiTokensService } from '@/services/provider/provider-api-tokens.service';

export const providerApiTokenKeys = {
  all: ['provider', 'api-tokens'] as const,
  list: () => [...providerApiTokenKeys.all, 'list'] as const,
  config: () => [...providerApiTokenKeys.all, 'config'] as const,
};

interface QueryOptions {
  enabled?: boolean;
}

export function useProviderApiTokens(options?: QueryOptions) {
  return useQuery({
    queryKey: providerApiTokenKeys.list(),
    queryFn: () => providerApiTokensService.list(),
    enabled: options?.enabled ?? true,
  });
}

export function useProviderSsoConfig(options?: QueryOptions) {
  return useQuery<ProviderSsoConfig | null>({
    queryKey: providerApiTokenKeys.config(),
    queryFn: () => providerApiTokensService.getSsoConfig(),
    enabled: options?.enabled ?? true,
  });
}

export function useCreateProviderApiToken() {
  const queryClient = useQueryClient();
  return useMutation<ProviderApiTokenCreateResponse, Error, ProviderApiTokenCreateInput>({
    mutationFn: (input) => providerApiTokensService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: providerApiTokenKeys.list() });
    },
  });
}

export function useRevokeProviderApiToken() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (tokenId) => providerApiTokensService.revoke(tokenId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: providerApiTokenKeys.list() });
    },
  });
}

export function useIssueCustomerPortalLink() {
  return useMutation<ProviderPortalLinkResponse, Error, ProviderPortalLinkPayload>({
    mutationFn: (payload) => providerApiTokensService.issueCustomerPortalLink(payload),
  });
}
